import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  Button,
  Heading,
  HStack,
  Icon,
  Text,
  VStack,
  Card,
  Badge,
  SimpleGrid,
  Spinner
} from '@chakra-ui/react'
import { FiCheckCircle, FiArrowRight, FiActivity, FiUsers, FiTrendingUp, FiAlertCircle } from 'react-icons/fi'
import { FaFacebook, FaGoogle } from 'react-icons/fa'
import { OnboardingLayout } from '../../components/onboarding/OnboardingLayout'
import { AdPreviewCard } from '../../components/onboarding/AdPreviewCard'
import { CarouselAdPreviewCard } from '../../components/onboarding/CarouselAdPreviewCard'
import { useOnboarding } from '../../contexts/OnboardingContext'
import { apiConfig } from '../../config/api'
import { logError, notifySupport, formatErrorForUser } from '../../utils/errorHandling'

export const ConfirmationPage: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { generated_assets, basic_info } = useOnboarding()
  const [campaignAd, setCampaignAd] = useState<{ headline: string; body: string } | null>(null)
  const [campaignData, setCampaignData] = useState<{ 
    selected_platforms?: string[]
    // Meta (Facebook/Instagram)
    meta_campaign_id?: string
    meta_ad_set_id?: string
    meta_error?: string
    meta_status?: string
    // Google Ads
    google_campaign_id?: string
    google_ad_group_id?: string
    google_error?: string
    google_status?: string
    // Common
    pack_id?: string
    impressions_total?: number
    duration_days?: number
    end_date?: string
    expected_end_date?: string
    is_separate_pack?: boolean
  } | null>(null)
  const [isLoadingCampaign, setIsLoadingCampaign] = useState(true)
  const [metaStatus, setMetaStatus] = useState<string | undefined>(undefined)
  const [metaError, setMetaError] = useState<string | undefined>(undefined)
  const [googleStatus, setGoogleStatus] = useState<string | undefined>(undefined)
  const [googleError, setGoogleError] = useState<string | undefined>(undefined)
  
  // Get payment info from navigation state
  const locationState = location.state as { 
    paymentIntentId?: string
    paymentSuccess?: boolean
    metaStatus?: string
    metaCampaignId?: string
    metaError?: string
    googleStatus?: string
    googleCampaignId?: string
    googleError?: string
  } | null
  
  // Try to get ad from context first, then fetch from campaign
  // For carousel flow, use carousel_candidates; for legacy flow, use selected_ad/ad_candidates
  const contextAd = generated_assets.selected_ad || generated_assets.ad_candidates?.[0]
  const selectedAd = campaignAd || contextAd
  const carouselCandidate = generated_assets.carousel_candidates?.[0]

  useEffect(() => {
    const confirmPurchaseAndFetchCampaign = async () => {
      try {
        const token = localStorage.getItem('firebase_id_token')
        if (!token) {
          setIsLoadingCampaign(false)
          return
        }

        // If we have paymentIntentId in state, call purchase/confirm to trigger campaign creation
        if (locationState?.paymentIntentId && !metaStatus && !googleStatus) {
          try {
            const confirmResponse = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/purchase/confirm`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ payment_intent_id: locationState.paymentIntentId })
            })

            if (confirmResponse.ok) {
              const result = await confirmResponse.json()
              
              // Update state with Meta status
              setMetaStatus(result.meta_status)
              setMetaError(result.meta_error)
              
              // Update state with Google status
              setGoogleStatus(result.google_status)
              setGoogleError(result.google_error)
              
              // Check Meta campaign creation status
              if (result.meta_status === 'failed') {
                const metaErrorMsg = result.meta_error
                if (metaErrorMsg) {
                  const error = new Error(metaErrorMsg)
                  logError(error, 'Meta campaign creation')
                  await notifySupport(error, 'Meta campaign creation', {
                    campaign_id: result.campaign_id,
                    meta_campaign_id: result.meta_campaign_id,
                    meta_status: result.meta_status
                  })
                }
              }
              
              // Check Google campaign creation status
              if (result.google_status === 'failed') {
                const googleErrorMsg = result.google_error
                if (googleErrorMsg) {
                  const error = new Error(googleErrorMsg)
                  logError(error, 'Google Ads campaign creation')
                  await notifySupport(error, 'Google Ads campaign creation', {
                    campaign_id: result.campaign_id,
                    google_campaign_id: result.google_campaign_id,
                    google_status: result.google_status
                  })
                }
              }
            }
          } catch (confirmError) {
            logError(confirmError, 'Purchase confirmation')
            setMetaStatus('failed')
            setMetaError(confirmError instanceof Error ? confirmError.message : 'Unknown error')
            setGoogleStatus('failed')
            setGoogleError(confirmError instanceof Error ? confirmError.message : 'Unknown error')
          }
        }

        // Fetch latest campaign data
        const response = await fetch(`${apiConfig.baseURL}/api/v1/spotlight/campaigns/latest`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (response.ok) {
          const campaign = await response.json()
          // Merge state (from purchase/confirm call) with fetched campaign data
          // State has the most recent status from the API call
          setCampaignData({
            selected_platforms: campaign.selected_platforms || ['meta'],
            // Meta (Facebook/Instagram)
            meta_campaign_id: locationState?.metaCampaignId || campaign.meta_campaign_id,
            meta_ad_set_id: campaign.meta_ad_set_id,
            meta_error: metaError || locationState?.metaError || campaign.meta_error,
            meta_status: metaStatus || locationState?.metaStatus || campaign.meta_status,
            // Google Ads
            google_campaign_id: locationState?.googleCampaignId || campaign.google_campaign_id,
            google_ad_group_id: campaign.google_ad_group_id,
            google_error: googleError || locationState?.googleError || campaign.google_error,
            google_status: googleStatus || locationState?.googleStatus || campaign.google_status,
            // Common
            pack_id: campaign.pack_id,
            impressions_total: campaign.impressions_total,
            duration_days: campaign.duration_days,
            end_date: campaign.end_date,
            expected_end_date: campaign.expected_end_date,
            is_separate_pack: campaign.is_separate_pack || false
          })
          // Extract creative from campaign - this is the source of truth
          if (campaign.creative?.headline && campaign.creative?.body) {
            setCampaignAd({
              headline: campaign.creative.headline,
              body: campaign.creative.body
            })
          } else {
            // Log if creative is missing for debugging (dev only)
            if (import.meta.env.DEV) {
              console.warn('Campaign creative not found', {
                campaign_id: campaign.id,
                has_creative: !!campaign.creative,
                creative_keys: campaign.creative ? Object.keys(campaign.creative) : null
              })
            }
          }
        } else {
          console.error('Failed to fetch campaign:', response.status, response.statusText)
        }
      } catch (error) {
        console.error('Failed to fetch campaign:', error)
      } finally {
        setIsLoadingCampaign(false)
      }
    }

    // Call purchase/confirm if needed, then fetch campaign data
    confirmPurchaseAndFetchCampaign()
  }, []) // Only run once on mount

  return (
    <OnboardingLayout
      currentStep={5}
      totalSteps={5}
      stepLabels={['Basic Setup', 'Preview', 'Landing page', 'Targeting', 'Confirmation']}
    >
      <VStack gap={8} align="stretch" maxW="5xl" mx="auto" px={{ base: 4, sm: 6 }}>
        {/* Success Header */}
        <VStack gap={4} textAlign="center" pt={4}>
          <Icon as={FiCheckCircle} color="green.500" boxSize={16} />
          <Heading size="2xl" color="gray.900">
            Payment Successful!
          </Heading>
          <Text fontSize="xl" color="gray.600" maxW="2xl">
            Your ad campaigns have been created and are ready for review.
          </Text>
        </VStack>

        {/* Status Card */}
        <Card.Root 
          bg="white" 
          borderColor="gray.200" 
          borderWidth="1px"
        >
          <Card.Body p={6}>
            <VStack align="start" gap={4}>
              {isLoadingCampaign ? (
                <HStack gap={2}>
                  <Spinner size="sm" />
                  <Text fontWeight="semibold" color="gray.700">
                    Creating campaigns on your selected platforms...
                  </Text>
                </HStack>
              ) : (
                <VStack align="start" gap={4} w="full">
                  {/* Platform Status Cards */}
                  <VStack gap={3} w="full">
                    {/* Meta Status */}
                    {campaignData?.selected_platforms?.includes('meta') && (
                      <Box 
                        p={4} 
                        borderRadius="md" 
                        borderWidth="1px"
                        borderColor={campaignData?.meta_status === 'failed' ? 'red.200' : 'green.200'}
                        bg={campaignData?.meta_status === 'failed' ? 'red.50' : 'green.50'}
                        w="full"
                      >
                        <HStack gap={3} mb={2}>
                          <Icon as={FaFacebook} color="blue.600" boxSize={5} />
                          <Text fontWeight="semibold" color="gray.800">Meta (Facebook & Instagram)</Text>
                          {campaignData?.meta_status === 'failed' ? (
                            <Badge colorPalette="red" variant="solid">FAILED</Badge>
                          ) : (
                            <Badge colorPalette="green" variant="solid">DRAFT CREATED</Badge>
                          )}
                        </HStack>
                        {campaignData?.meta_status === 'failed' ? (
                          <Text fontSize="sm" color="red.700">
                            {campaignData.meta_error 
                              ? formatErrorForUser(new Error(campaignData.meta_error), 'Meta campaign creation')
                              : 'Campaign creation failed. Please contact support.'}
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="green.700">
                            Campaign created and awaiting Meta review. {campaignData?.meta_campaign_id && `ID: ${campaignData.meta_campaign_id}`}
                          </Text>
                        )}
                      </Box>
                    )}

                    {/* Google Ads Status */}
                    {campaignData?.selected_platforms?.includes('google') && (
                      <Box 
                        p={4} 
                        borderRadius="md" 
                        borderWidth="1px"
                        borderColor={campaignData?.google_status === 'failed' ? 'red.200' : 'green.200'}
                        bg={campaignData?.google_status === 'failed' ? 'red.50' : 'green.50'}
                        w="full"
                      >
                        <HStack gap={3} mb={2}>
                          <Icon as={FaGoogle} color="red.500" boxSize={5} />
                          <Text fontWeight="semibold" color="gray.800">Google Ads</Text>
                          {campaignData?.google_status === 'failed' ? (
                            <Badge colorPalette="red" variant="solid">FAILED</Badge>
                          ) : (
                            <Badge colorPalette="green" variant="solid">DRAFT CREATED</Badge>
                          )}
                        </HStack>
                        {campaignData?.google_status === 'failed' ? (
                          <Text fontSize="sm" color="red.700">
                            {campaignData.google_error 
                              ? formatErrorForUser(new Error(campaignData.google_error), 'Google Ads campaign creation')
                              : 'Campaign creation failed. Please contact support.'}
                          </Text>
                        ) : (
                          <Text fontSize="sm" color="green.700">
                            Campaign created and awaiting Google review. {campaignData?.google_campaign_id && `ID: ${campaignData.google_campaign_id}`}
                          </Text>
                        )}
                      </Box>
                    )}
                  </VStack>

                  {/* Review Process Info */}
                  {!isLoadingCampaign && (campaignData?.meta_status !== 'failed' || campaignData?.google_status !== 'failed') && (
                    <Box bg="blue.50" borderColor="blue.200" borderWidth="1px" borderRadius="md" p={4} w="full">
                      <Text fontSize="sm" color="blue.800" fontWeight="medium" mb={1}>
                        Ad Review Process
                      </Text>
                      <Text fontSize="sm" color="blue.700">
                        Ad platforms typically review ads within 24 hours. Once approved, your campaigns will <strong>automatically activate</strong> and start running. You'll receive an email notification when approval is complete.
                      </Text>
                    </Box>
                  )}

                  <Text fontSize="sm" color="gray.600">
                    You can pause or manage your campaigns anytime from your dashboard.
                  </Text>
                  
                  {/* Campaign Duration Info */}
                  {campaignData?.duration_days && (
                    <Box bg="purple.50" borderColor="purple.200" borderWidth="1px" borderRadius="md" p={3} w="full">
                      <Text fontSize="xs" color="purple.600" fontWeight="medium" mb={1}>
                        Campaign Duration
                      </Text>
                      <Text fontSize="sm" color="purple.700">
                        Your campaigns will run for <strong>{campaignData.duration_days} days</strong> once approved and activated.
                        {campaignData.end_date && (
                          <> Expected end: <strong>{new Date(campaignData.end_date).toLocaleDateString()}</strong>.</>
                        )}
                        {campaignData.expected_end_date && !campaignData.end_date && (
                          <> Expected end: <strong>{new Date(campaignData.expected_end_date).toLocaleDateString()}</strong> (estimated).</>
                        )}
                      </Text>
                    </Box>
                  )}
                </VStack>
              )}
            </VStack>
          </Card.Body>
        </Card.Root>

        <SimpleGrid columns={{ base: 1, md: 2 }} gap={8}>
          {/* Ad Preview */}
          <VStack align="stretch" gap={4}>
            <Heading size="md" color="gray.900">
              Your Ad Draft
            </Heading>
            {isLoadingCampaign ? (
              <Box p={6} bg="gray.50" borderRadius="lg" display="flex" justifyContent="center" alignItems="center">
                <Spinner size="md" color="brand.500" />
              </Box>
            ) : carouselCandidate ? (
              <Box maxW="400px">
                <CarouselAdPreviewCard
                  practitionerName={basic_info?.name || ''}
                  practitionerPhotoUrl={basic_info?.photo_url || ''}
                  practitionerPhotoCutoutUrl={(basic_info as any)?.photo_cutout_url}
                  whoYouHelp={basic_info?.who_you_help || ''}
                  primaryOutcome={basic_info?.offer_primary_outcome || ''}
                  templateName={carouselCandidate.template_name}
                  slides={carouselCandidate.slides}
                  bgPalette={(basic_info as any)?.carousel_bg_palette || 'brand'}
                  photoFocalX={(basic_info as any)?.photo_focal_x}
                  photoFocalY={(basic_info as any)?.photo_focal_y}
                  photoScale={(basic_info as any)?.photo_scale}
                  photoFitMode={(basic_info as any)?.photo_fit_mode}
                  photoGradientSide={(basic_info as any)?.photo_gradient_side}
                  photoReveal={(basic_info as any)?.photo_reveal}
                  identityTextLayout={(basic_info as any)?.identity_text_layout}
                  photoMode={(basic_info as any)?.photo_mode ?? 'original'}
                  photoShadow={(basic_info as any)?.photo_shadow}
                />
              </Box>
            ) : selectedAd ? (
              <AdPreviewCard
                headline={selectedAd.headline}
                body={selectedAd.body}
                isEditable={false}
              />
            ) : (
              <Box p={6} bg="gray.50" borderRadius="lg">
                <Text color="gray.500">Ad preview not available</Text>
              </Box>
            )}
          </VStack>

          {/* Projections */}
          <VStack align="stretch" gap={4}>
            <Heading size="md" color="gray.900">
              Projected Performance
            </Heading>
            <Card.Root>
              <Card.Body p={6}>
                {(() => {
                  // Calculate projections based on campaign data
                  const campaignImpressions = campaignData?.impressions_total || 0
                  const platformCount = campaignData?.selected_platforms?.length || 1
                  const isSeparatePacks = campaignData?.is_separate_pack
                  
                  // For separate packs: each campaign has full impressions, and there are multiple campaigns
                  // For split packs: single campaign with impressions split across platforms
                  // If is_separate_pack is true, assume 2 campaigns (one per platform) since we only support 2 platforms
                  const totalImpressionsAcrossPlatforms = isSeparatePacks
                    ? campaignImpressions * 2  // 2 separate campaigns, each with full impressions
                    : campaignImpressions  // Single campaign (split or single platform)
                  
                  // Projection calculations (industry averages)
                  // Reach: 50-80% of impressions (some people see ad multiple times)
                  const reachMin = Math.round(totalImpressionsAcrossPlatforms * 0.5)
                  const reachMax = Math.round(totalImpressionsAcrossPlatforms * 0.8)
                  
                  // CTR: 1-3% for social/display ads
                  const ctrMin = 0.01
                  const ctrMax = 0.03
                  const clicksMin = Math.round(totalImpressionsAcrossPlatforms * ctrMin)
                  const clicksMax = Math.round(totalImpressionsAcrossPlatforms * ctrMax)
                  
                  // Lead conversion: 10-20% of clicks
                  const leadConvMin = 0.10
                  const leadConvMax = 0.20
                  const leadsMin = Math.round(clicksMin * leadConvMin)
                  const leadsMax = Math.round(clicksMax * leadConvMax)
                  
                  return (
                    <VStack gap={6} align="stretch">
                      <HStack justify="space-between">
                        <HStack gap={3}>
                          <Box p={2} bg="brand.50" borderRadius="md">
                            <Icon as={FiUsers} color="brand.500" />
                          </Box>
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" color="gray.600">Est. Reach</Text>
                            <Text fontWeight="bold" fontSize="lg">
                              {campaignImpressions > 0 
                                ? `~${reachMin.toLocaleString()} - ${reachMax.toLocaleString()}`
                                : '~2,500 - 5,000'}
                            </Text>
                          </VStack>
                        </HStack>
                      </HStack>
                      
                      <HStack justify="space-between">
                        <HStack gap={3}>
                          <Box p={2} bg="purple.50" borderRadius="md">
                            <Icon as={FiActivity} color="purple.500" />
                          </Box>
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" color="gray.600">Est. Clicks</Text>
                            <Text fontWeight="bold" fontSize="lg">
                              {campaignImpressions > 0
                                ? `~${clicksMin.toLocaleString()} - ${clicksMax.toLocaleString()}`
                                : '~150 - 300'}
                            </Text>
                          </VStack>
                        </HStack>
                      </HStack>

                      <HStack justify="space-between">
                        <HStack gap={3}>
                          <Box p={2} bg="green.50" borderRadius="md">
                            <Icon as={FiTrendingUp} color="green.500" />
                          </Box>
                          <VStack align="start" gap={0}>
                            <Text fontSize="sm" color="gray.600">Est. Leads</Text>
                            <Text fontWeight="bold" fontSize="lg">
                              {campaignImpressions > 0
                                ? `~${leadsMin.toLocaleString()} - ${leadsMax.toLocaleString()}`
                                : '~15 - 40'}
                            </Text>
                          </VStack>
                        </HStack>
                      </HStack>
                      
                      {campaignImpressions > 0 && (
                        <Box bg="blue.50" p={3} borderRadius="md" borderWidth="1px" borderColor="blue.200">
                          <Text fontSize="xs" color="blue.700">
                            <strong>Based on {totalImpressionsAcrossPlatforms.toLocaleString()} total impressions</strong>
                            {isSeparatePacks 
                              ? ` across 2 separate campaigns (${campaignImpressions.toLocaleString()} per campaign)`
                              : platformCount > 1
                                ? ` split ${platformCount === 2 ? '50/50' : 'evenly'} across ${platformCount} platform${platformCount > 1 ? 's' : ''}`
                                : ` on ${platformCount === 1 && campaignData?.selected_platforms?.[0] === 'meta' ? 'Meta' : campaignData?.selected_platforms?.[0] === 'google' ? 'Google Ads' : 'your selected platform'}`
                            }
                          </Text>
                        </Box>
                      )}
                    </VStack>
                  )
                })()}
              </Card.Body>
            </Card.Root>
            
            <Text fontSize="xs" color="gray.500" fontStyle="italic">
              *Estimates based on typical performance in your category. Actual results may vary.
            </Text>
          </VStack>
        </SimpleGrid>

        {/* Action */}
        <Box pt={8} display="flex" justifyContent="center">
          <Button
            size="xl"
            colorPalette="brand"
            rightIcon={<Icon as={FiArrowRight} />}
            onClick={() => navigate('/dashboard')}
            w={{ base: 'full', sm: 'auto' }}
            px={12}
          >
            Go to Dashboard
          </Button>
        </Box>
      </VStack>
    </OnboardingLayout>
  )
}



